import { LinkAttributes } from '@/types'
import Image from '@tiptap/extension-image'
import { Editor, mergeAttributes } from '@tiptap/vue-3'
import { isEmpty } from 'lodash'
import { Fragment } from 'prosemirror-model'
import { ref } from 'vue'

type ImageAttributes = {
  src: string
  alt?: string
  title?: string
  width?: string | undefined
  height?: string | undefined
  style?: string
  class?: string
}

export const ExtendedImage = Image.extend({
  addAttributes() {
    return {
      src: {
        default: '',
      },
      alt: {
        default: undefined,
      },
      title: {
        default: undefined,
      },
      width: {
        default: undefined,
      },
      height: {
        default: undefined,
      },
      style: {
        default: undefined,
      },
      class: {
        default: 'rounded-lg',
      },
      'data-loading': {
        default: 'true',
      },
    }
  },

  renderHTML({ HTMLAttributes }) {
    const { 'data-loading': dataLoading, style, ...rest } = HTMLAttributes

    // Parse existing styles into an object
    const existingStyles =
      style
        ?.split(';')
        .filter(Boolean)
        .reduce((acc, curr) => {
          const [key, value] = curr.split(':').map(s => s.trim())
          if (key && value) acc[key] = value
          return acc
        }, {}) || {}

    const newStyle = {
      ...existingStyles,
      opacity: dataLoading === 'true' ? '0.5' : '1',
    }

    const styleString = Object.entries(newStyle)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ')

    return [
      'img',
      mergeAttributes(rest, {
        'data-loading': dataLoading,
        style: styleString,
      }),
    ]
  },

  addNodeView() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return ({ node, HTMLAttributes, getPos }) => {
      const img = document.createElement('img')
      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        img.setAttribute(key, value)
      })

      // Add loading animation class
      img.classList.add('gif-image-loading')

      // Handle image load
      img.onload = () => {
        if (typeof getPos === 'function') {
          const pos = getPos()
          const { view } = this.editor

          view.dispatch(
            view.state.tr.setNodeAttribute(pos, 'data-loading', 'false')
          )
        }
        img.classList.remove('gif-image-loading')
      }

      return {
        dom: img,
      }
    }
  },
})

export function useEditor(editor?: Editor) {
  if (!editor) {
    throw 'Editor is not available!'
  }

  const linkValues = ref<LinkAttributes | undefined>(undefined)

  //add emoji class and wrap emoji in span tag to style it
  const onEmojiAdd = (emoji: string) => {
    editor
      ?.chain()
      .focus()
      .setMark('textStyle', { class: 'emoji' })
      .insertContent(emoji)
      .unsetMark('textStyle')
      .run()
  }

  const onGifAdd = (
    gifUrl: string,
    _width?: number,
    _height?: number,
    maxWidth?: string,
    type?: string
  ) => {
    // Calculate the height based on the aspect ratio, to set height and maxHeight to avoid content layout shift
    const aspectRatio = _width && _height ? _width / _height : 1
    const parsedMaxWidth = maxWidth ? parseInt(maxWidth, 10) : 480
    let maxHeight

    if (type === 'comment') {
      maxHeight = parsedMaxWidth / aspectRatio
    }

    const imageAttributes: ImageAttributes = {
      src: gifUrl,
      alt: '',
      title: '',
      width: _width ? `${_width}px` : '100%',
      height: _height ? `${_height}px` : 'auto',
      style: `${maxWidth ? ` max-width: ${maxWidth};` : ''}${
        maxHeight ? ` max-height: ${maxHeight}px;` : ''
      }`,
      class:
        type === 'comment'
          ? 'communities-giphy-image-comment'
          : 'communities-giphy-image-post',
    }

    const { schema } = editor.state
    const imageNode = schema.nodes.image.create(imageAttributes)
    const paragraphNode = schema.nodes.paragraph.create()

    // Create a fragment with the image and an empty paragraph
    const fragment = Fragment.fromArray([imageNode, paragraphNode])

    editor
      .chain()
      .focus()
      .command(({ tr, dispatch }) => {
        const { selection } = editor.state
        const position = selection.to
        const transaction = tr.insert(position, fragment)

        if (dispatch) {
          dispatch(transaction)
        }

        // Move cursor after the inserted content and scroll into view
        const newPosition = position + fragment.size
        editor.commands.setTextSelection(newPosition)

        // Scroll the cursor into view after a short delay to ensure DOM is updated
        setTimeout(() => {
          const domNode = editor.view.domAtPos(newPosition)?.node as HTMLElement
          domNode?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }, 100)

        return true
      })
      .run()
  }

  //ToDo: add types for the params
  const onLinkAdd = (linkData: any) => {
    if (linkData.href) {
      const { from, to } = editor.view.state.selection
      editor
        .chain()
        .setTextSelection({ from, to })
        .setLink({ href: linkData.href, target: '_blank', rel: 'nofollow' })
        .insertContent(linkData.text || linkData.href)
        .unsetMark('link')
        .insertContent(' ')
        .focus()
        .run()
    } else {
      editor?.chain().focus().unsetLink().run()
    }
  }

  const addEmptyLink = (linkData: any) => {
    linkValues.value = { href: '', text: linkData.text }
  }

  const onHashtagAdd = () => {
    const { selection } = editor.state
    const { $from } = selection

    // Check if the character before the cursor is already a '#'
    const textBefore = $from.nodeBefore?.text || ''
    const charBefore = textBefore.slice(-1)

    // If the last character is already '#', don't insert another one
    if (charBefore === '#') {
      editor.chain().focus().run()
      return
    }

    editor.chain().focus().insertContent('#').run()
  }

  editor?.on('transaction', ({ editor }) => {
    const linkAttributes = editor.getAttributes('link') as LinkAttributes

    linkValues.value = undefined

    if (!isEmpty(linkAttributes)) {
      linkValues.value = linkAttributes
    }
  })

  return {
    linkValues,
    onEmojiAdd,
    onGifAdd,
    onLinkAdd,
    addEmptyLink,
    onHashtagAdd,
  }
}
